import { IconProps } from '@tabler/icons-react'
import { ComponentType, ReactNode } from 'react'

import { AstraAvatar } from '../AstraAvatar/AstraAvatar'
import { AstraChip } from '../AstraChip/AstraChip'
import { AstraIcon, IconComponent } from '../AstraIcon/AstraIcon'
import { AstraTooltip } from '../AstraTooltip/AstraTooltip'
import { AstraParagraph } from '../Typography/AstraParagraph/AstraParagraph'

export function getInitials(username: string) {
    if (!username) return ''
    const names = username.split(' ')
    const firstName = names.at(0)
    if (!firstName) return ''
    const lastName = names.at(-1)
    if (!lastName || firstName === lastName) return firstName.substring(0, 2).toUpperCase()
    return firstName.charAt(0).toUpperCase() + lastName.charAt(0).toUpperCase()
}

export type AstraActivityLogEntry = {
    activityIcon: ComponentType<IconProps> | IconComponent
    /**
     * Name of the user who performed the activity
     */
    changedBy: string | undefined | null
    /**
     * Timestamp of when the activity occurred
     */
    changedAt: string | undefined | null
    /**
     * Main content of the activity log entry
     * F.e. comment text
     */
    content?: ReactNode
    /**
     * Icon component to display as the info icon (e.g., source icon)
     * If provided, will render inside a AstraIcon component
     * If not provided, a AstraAvatar with initials will be shown instead
     */
    infoIcon?: ComponentType<IconProps> | IconComponent
    /**
     * Additional info content to display alongside the infoIcon
     * F.e. action performed like "added a comment"
     */
    infoContent?: ReactNode
}

type StructuredEntryProps = {
    entry: AstraActivityLogEntry
    isLatest?: boolean
    formatDate: (date: string) => string
}

export const AstraActivityLogStructuredEntryComponent = ({ entry, isLatest, formatDate }: StructuredEntryProps) => {
    const { changedBy, changedAt, content, activityIcon, infoContent, infoIcon } = entry

    return (
        <div className="grid grid-cols-[44px_1fr_auto] grid-rows-[auto_1fr] gap-2">
            <div className="relative row-span-2 flex h-full flex-col items-center justify-center self-start">
                <div className="pointer-events-none absolute -top-6 h-6 w-[2px] bg-neutral-rest" />
                <div>
                    <AstraChip icon={<AstraIcon icon={activityIcon} />} size="medium" variant="filled" />
                </div>
                <div className="pointer-events-none h-full w-[2px] bg-neutral-rest group-last:bg-transparent" />
            </div>

            <div className="flex items-center gap-2">
                <AstraTooltip tooltipContent={changedBy || 'Ukjent bruker'}>
                    {infoIcon ? (
                        <AstraIcon icon={infoIcon} />
                    ) : (
                        <AstraAvatar initials={getInitials(changedBy || '')} size="small" variant="initial" useColorGenerator={!!changedBy} />
                    )}
                </AstraTooltip>
                <AstraParagraph variant="body" weight="regular" as="span">
                    {infoContent}
                </AstraParagraph>
            </div>

            {changedAt && (
                <div className="flex items-start justify-end">
                    <AstraParagraph variant="body-small" weight="regular" className="text-right text-muted">
                        {formatDate(changedAt)}
                    </AstraParagraph>
                </div>
            )}

            <AstraParagraph weight="regular" variant="body" className={`col-start-2 col-end-4 ${isLatest ? 'text-default' : 'text-muted'}`} as="span">
                {content}
            </AstraParagraph>
        </div>
    )
}
